import { useEffect } from 'react';
import './success-toast.css';

/**
 * Props para SuccessToast
 */
interface SuccessToastProps {
  /**
   * Mensaje de éxito a mostrar
   */
  message: string | null;

  /**
   * Callback cuando el toast se cierra automáticamente
   */
  onClose: () => void;

  /**
   * Duración en ms antes de auto-cerrar
   * @default 3000
   */
  duration?: number;
}

/**
 * SuccessToast - Notificación de éxito que emerge desde arriba
 *
 * @version 1.0
 * @updated 2026-02-16
 * @module core/components
 *
 * @description
 * Toast de éxito compacto que emerge desde el centro superior
 * con animación de entrada/salida vertical.
 *
 * @remarks
 * **Características**:
 * - Posicionamiento fijo centrado (position: fixed, left: 50%)
 * - Animación slideDown al aparecer
 * - Animación slideUp al desaparecer
 * - Auto-cierre después de 3s (configurable)
 * - Glassmorphism en estilo de éxito (verde)
 * - Compacto: max-width 400px
 * - Sin emojis (cumple manifiesto)
 *
 * **Cumple con**:
 * - P1: Componente <100 líneas
 * - P3: Documentación JSDoc completa
 * - P6: Variables CSS (no hardcoded)
 * - Manifiesto: Sin emojis decorativos
 *
 * @example
 * ```tsx
 * <SuccessToast
 *   message="Anotación guardada exitosamente"
 *   onClose={() => setSuccess(null)}
 * />
 * ```
 */
export function SuccessToast({
  message,
  onClose,
  duration = 3000,
}: SuccessToastProps): JSX.Element | null {
  // Auto-cerrar después de la duración especificada
  useEffect(() => {
    if (!message) return;

    const timer = setTimeout(() => {
      onClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [message, duration, onClose]);

  if (!message) return null;

  return (
    <div className="success-toast" role="status" aria-live="polite">
      <div className="success-toast-content">
        <span className="success-toast-message">{message}</span>
        <button
          className="success-toast-close"
          onClick={onClose}
          aria-label="Cerrar notificación"
          type="button"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
